import type { RouteObject } from 'react-router';

import { Outlet } from 'react-router';
import { lazy, Suspense } from 'react';

import { CONFIG } from 'src/global-config';
import { DashboardLayout } from 'src/layouts/dashboard';

import { LoadingScreen } from 'src/components/loading-screen';

import { AccountLayout } from 'src/sections/account/account-layout';

import {AuthGuard, RoleGuard} from 'src/auth/guard';

import { useSessionHeartbeat } from 'src/utils/use-session-heartbeat';

import { usePathname } from '../hooks';

// ----------------------------------------------------------------------

// Overview
const IndexPage = lazy(() => import('src/pages/dashboard'));

// User
const UserListPage = lazy(() => import('src/pages/dashboard/user/list'));
const UserAggregationPage = lazy(() => import('src/pages/dashboard/user/aggregation'));
const UserCreatePage = lazy(() => import('src/pages/dashboard/user/new'));
const UserEditPage = lazy(() => import('src/pages/dashboard/user/edit'));
const UserDetailPage = lazy(() => import('src/pages/dashboard/user/profile'));
const UserCampiExtraListPage = lazy(() => import('src/pages/dashboard/user/campiExtraList'));
const UserCreateCampiExtraPage = lazy(() => import('src/pages/dashboard/user/newCampoExtra'));
const UserEditCampiExtraPage = lazy(() => import('src/pages/dashboard/user/editCampoExtra'));

//Consulente
const ConsulenteListPage = lazy(() => import('src/pages/dashboard/consulente/list'));
const ConsulenteCreatePage = lazy(() => import('src/pages/dashboard/consulente/new'));
const ConsulenteEditPage = lazy(() => import('src/pages/dashboard/consulente/edit'));
const ConsulenteDetailPage = lazy(() => import('src/pages/dashboard/consulente/profile'));


// Notification
const NotificationListPage = lazy(() => import('src/pages/dashboard/notification/list'));
// const NotificationCreatePage = lazy(() => import('src/pages/dashboard/notification/new'));

// Compilazioni
const CompilazioniListPage = lazy(() => import('src/pages/dashboard/compilazione/list'));
const CompilazioneCreatePage = lazy(() => import('src/pages/dashboard/compilazione/new'));
const CompilazioneEditPage = lazy(() => import('src/pages/dashboard/compilazione/edit'));
const CompilazioneDetailPage = lazy(() => import('src/pages/dashboard/compilazione/details'));

// Articoli
const ArticoliListPage = lazy(() => import('src/pages/dashboard/articoli/list'));
const ArticoloDetailPage = lazy(() => import('src/pages/dashboard/articoli/detail'));

// Avatar del cliente (letto da Monday): solo Amministratore e Backoffice
const AvatarPage = lazy(() => import('src/pages/dashboard/avatar'));

// Mappa dei clienti: solo Amministratore e Backoffice
const MappaClientiPage = lazy(() => import('src/pages/dashboard/mappa-clienti'));

// Carica cliente da screenshot
const ClienteScreenPage = lazy(() => import('src/pages/dashboard/cliente-screen'));

// Corso CRM
const CorsoCrmPage = lazy(() => import('src/pages/dashboard/corso-crm'));

// Forecast mensile
const ForecastPage = lazy(() => import('src/pages/dashboard/forecast'));

// Business Training (incontri registrati) e relativa gestione per lo staff
const BusinessTrainingPage = lazy(() => import('src/pages/dashboard/business-training'));
const BusinessTrainingGestionePage = lazy(() => import('src/pages/dashboard/business-training-gestione'));

// Area Manager
const ClientiDirettiAssegnazionePage = lazy(() => import('src/pages/dashboard/clienti-diretti-assegnazione'));
const ClientiDirettiDistribuzionePage = lazy(() => import('src/pages/dashboard/clienti-diretti-distribuzione'));
const FirmaSchedaPage = lazy(() => import('src/pages/dashboard/area-manager-firma-scheda'));
const SchedaBeginnerPage = lazy(() => import('src/pages/dashboard/area-manager-scheda-beginner'));
const ReclutamentoPage = lazy(() => import('src/pages/dashboard/area-manager-reclutamento'));

// Vista Beginner: il beginner segue il percorso di avviamento (solo ruolo Beginner)
const BeginnerPanoramicaPage = lazy(() => import('src/pages/dashboard/beginner'));
const BeginnerDocumentiPage = lazy(() => import('src/pages/dashboard/beginner/documenti'));
const BeginnerSchedaPage = lazy(() => import('src/pages/dashboard/beginner/scheda'));
const BeginnerOcfPage = lazy(() => import('src/pages/dashboard/beginner/ocf'));
// Formazione: nascosta per ora (v. sections/vista-beginner/view/beginner-formazione-view.tsx)
// const BeginnerFormazionePage = lazy(() => import('src/pages/dashboard/beginner/formazione'));

// Gestione dei beginner (solo Amministratore e Backoffice)
const BeginnerGestioneListPage = lazy(() => import('src/pages/dashboard/beginner-gestione/list'));
const BeginnerGestioneDetailPage = lazy(() => import('src/pages/dashboard/beginner-gestione/detail'));

// Staff: plancia mattutina di Cristian (PAC, proposte, ribilanciamenti, fondo pensione, diagnosi, reminder, onboarding)
const PlanciaStaffPage = lazy(() => import('src/pages/dashboard/staff-plancia'));

// Staff: Operatività Miche (board Monday "GANTT intelligente")
const GanttPage = lazy(() => import('src/pages/dashboard/staff-gantt'));

// Staff: Plancia Francesco (post, storie, reminder, feedback, reclutamento; costo per follower)
const PlanciaPage = lazy(() => import('src/pages/dashboard/social-plancia'));

// Staff: Piano editoriale social (Management Post + Idee Social su Monday)
const PianoEditorialePage = lazy(() => import('src/pages/dashboard/staff-piano-editoriale'));
const FirmaTeamPage = lazy(() => import('src/pages/dashboard/area-manager-firma-team'));

// Firma PDF (Gestione)
const FirmaPdfPage = lazy(() => import('src/pages/dashboard/gestione-firma-pdf'));

// Permessi dei consulenti e spegnimento d'emergenza (solo Amministratore e Backoffice)
const PermessiPage = lazy(() => import('src/pages/dashboard/permessi'));
const EmergenzaPage = lazy(() => import('src/pages/dashboard/emergenza'));

// Editor dei testi dei template PDF (solo Amministratore e Backoffice)
const TestiPdfPage = lazy(() => import('src/pages/dashboard/testi-pdf'));

// Statistiche
const StatistichePage = lazy(() => import('src/pages/dashboard/statistiche'));
const StatisticheAppPage = lazy(() => import('src/pages/dashboard/statistiche-app'));

// Eventi
const EventiListPage = lazy(() => import('src/pages/dashboard/eventi/list'));
const EventoDetailPage = lazy(() => import('src/pages/dashboard/eventi/detail'));

// Group
const GruppoListPage = lazy(() => import('src/pages/dashboard/group/list'));
const GruppoCreatePage = lazy(() => import('src/pages/dashboard/group/new'));
const GruppoEditPage = lazy(() => import('src/pages/dashboard/group/edit'));
const GruppoDetailPage = lazy(() => import('src/pages/dashboard/group/detail'));

const AccountGeneralPage = lazy(() => import('src/pages/dashboard/account/general'));
const AccountChangePasswordPage = lazy(() => import('src/pages/dashboard/account/change-password'));
const AccountNotifications = lazy(() => import('src/pages/dashboard/account/notifications'));
const AccountSettingsUIConsulenti = lazy(() => import('src/pages/dashboard/account/settingsuiconsulenti'));
const AccountPortafogliModello = lazy(() => import('src/pages/dashboard/account/portafogli-modello'));

function SuspenseOutlet() {
  const pathname = usePathname();

  return (
    <Suspense key={pathname} fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  );
}

// Monta una volta sola per l'intera sessione autenticata (non dentro
// SuspenseOutlet, riusato anche da accountLayout: farebbe doppio conteggio).
function DashboardLayoutWithHeartbeat() {
  useSessionHeartbeat();

  return (
    <DashboardLayout>
      <SuspenseOutlet />
    </DashboardLayout>
  );
}

const dashboardLayout = () => <DashboardLayoutWithHeartbeat />;

const accountLayout = () => (
  <AccountLayout>
    <SuspenseOutlet />
  </AccountLayout>
);

export const dashboardRoutes: RouteObject[] = [
  {
    path: '/',
    element: CONFIG.auth.skip ? dashboardLayout() : <AuthGuard>{dashboardLayout()}</AuthGuard>,
    children: [
      { index: true, element: <IndexPage /> },
      {
        path: 'cliente',
        element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice']} />,
        children: [
          { index: true, path: 'list', element: <UserListPage /> },
          { path: 'aggregation', element: <UserAggregationPage /> },
          { path: 'new', element: <UserCreatePage /> },
          { path: ':id/profile', element: <UserDetailPage /> },
          { path: ':id/edit', element: <UserEditPage /> },
          { path: 'campiExtra', element: <UserCampiExtraListPage /> },
          { path: 'campoExtra/new', element: <UserCreateCampiExtraPage /> },
          { path: 'campoExtra/:id/edit', element: <UserEditCampiExtraPage /> },
        ],
      },

      {
        path: 'account',
        element: accountLayout(),
        children: [
          { index: true, element: <AccountGeneralPage /> },
          { path: 'change-password', element: <AccountChangePasswordPage /> },
          { path: 'notifications', element: <AccountNotifications /> },
          { path: 'settings-ui-consulenti',element:  <RoleGuard allowedRoles={['Consulente']} />, children:  [{ index: true, path: '', element: <AccountSettingsUIConsulenti /> }]},
        ],
      },
      {
        path: 'portafogli-modello',
        element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice']} />,
        children: [{ index: true, path: '', element: <AccountPortafogliModello /> }],
      },
      {
        path: 'consulente',
        element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice']} />,
        children: [
          { index: true, path: 'list', element: <ConsulenteListPage /> },
          { path: 'new', element: <ConsulenteCreatePage /> },
          { path: ':id/profile', element: <ConsulenteDetailPage /> },
          { path: ':id/edit', element: <ConsulenteEditPage /> },
        ],
      },

      {
        path: 'compilazione',
        children: [
          {
            element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice','Consulente']} risorsa="menu:compilazioni" children={undefined} />,
            children: [
              { index: true, path: 'list', element: <CompilazioniListPage /> },
              { path: ':id/edit', element: <CompilazioneEditPage /> },
              { path: ':id', element: <CompilazioneDetailPage /> },
              { path: 'new', element: <CompilazioneCreatePage /> }
            ],
          },
        ],
      },

      {
        path: 'articoli',
        element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice', 'Consulente']} risorsa="menu:articoli" />,
        children: [
          { index: true, element: <ArticoliListPage /> },
          { path: ':id', element: <ArticoloDetailPage /> },
        ],
      },

      {
        path: 'beginner',
        element: <RoleGuard allowedRoles={['Beginner']} />,
        children: [
          { index: true, element: <BeginnerPanoramicaPage /> },
          { path: 'documenti', element: <BeginnerDocumentiPage /> },
          { path: 'scheda', element: <BeginnerSchedaPage /> },
          // { path: 'formazione', element: <BeginnerFormazionePage /> },
          { path: 'ocf', element: <BeginnerOcfPage /> },
        ],
      },

      {
        path: 'beginner-gestione',
        element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice']} />,
        children: [
          { index: true, element: <BeginnerGestioneListPage /> },
          { path: ':id', element: <BeginnerGestioneDetailPage /> },
        ],
      },

      {
        path: 'avatar',
        element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice']} />,
        children: [{ index: true, element: <AvatarPage /> }],
      },

      {
        path: 'mappa-clienti',
        element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice']} />,
        children: [{ index: true, element: <MappaClientiPage /> }],
      },

      {
        path: 'cliente-screen',
        element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice', 'Consulente']} strumento="cliente-screen" risorsa="menu:carica-cliente" />,
        children: [{ index: true, element: <ClienteScreenPage /> }],
      },

      {
        path: 'permessi-consulenti',
        element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice']} />,
        children: [{ index: true, element: <PermessiPage /> }],
      },

      {
        path: 'testi-pdf',
        element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice']} />,
        children: [{ index: true, element: <TestiPdfPage /> }],
      },

      {
        path: 'emergenza-piattaforma',
        element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice']} />,
        children: [{ index: true, element: <EmergenzaPage /> }],
      },

      {
        path: 'corso-crm',
        element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice', 'Consulente']} risorsa="menu:corso-crm" />,
        children: [{ index: true, element: <CorsoCrmPage /> }],
      },

      {
        path: 'forecast',
        element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice', 'Consulente']} risorsa="menu:forecast" />,
        children: [{ index: true, element: <ForecastPage /> }],
      },

      {
        path: 'business-training',
        children: [
          {
            element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice', 'Consulente']} risorsa="menu:business-training" />,
            children: [{ index: true, element: <BusinessTrainingPage /> }],
          },
          {
            path: 'gestione',
            element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice']} />,
            children: [{ index: true, element: <BusinessTrainingGestionePage /> }],
          },
        ],
      },

      {
        path: 'area-manager/clienti-diretti',
        element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice']} />,
        children: [
          { path: 'assegnazione', element: <ClientiDirettiAssegnazionePage /> },
          { path: 'distribuzione', element: <ClientiDirettiDistribuzionePage /> },
        ],
      },

      {
        path: 'area-manager/firma-scheda',
        element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice']} />,
        children: [{ index: true, element: <FirmaSchedaPage /> }],
      },

      {
        path: 'area-manager/firma-accordi-team',
        element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice']} />,
        children: [{ index: true, element: <FirmaTeamPage /> }],
      },

      {
        path: 'firma-pdf',
        element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice', 'Consulente']} strumento="firma-pdf" risorsa="menu:firma-pdf" />,
        children: [{ index: true, element: <FirmaPdfPage /> }],
      },

      {
        path: 'area-manager/scheda-beginner',
        element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice']} />,
        children: [{ index: true, element: <SchedaBeginnerPage /> }],
      },

      {
        path: 'area-manager/reclutamento/dashboard',
        element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice']} />,
        children: [{ index: true, element: <ReclutamentoPage /> }],
      },

      {
        path: 'staff/plancia',
        element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice']} />,
        children: [{ index: true, element: <PlanciaStaffPage /> }],
      },

      {
        path: 'staff/gantt',
        element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice']} />,
        children: [{ index: true, element: <GanttPage /> }],
      },

      {
        path: 'staff/plancia-francesco',
        element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice']} />,
        children: [{ index: true, element: <PlanciaPage /> }],
      },

      {
        path: 'staff/piano-editoriale',
        element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice']} />,
        children: [{ index: true, element: <PianoEditorialePage /> }],
      },

      {
        path: 'eventi',
        element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice', 'Consulente']} risorsa="menu:eventi" />,
        children: [
          { index: true, element: <EventiListPage /> },
          { path: ':id', element: <EventoDetailPage /> },
        ],
      },

      {
        path: 'statistiche',
        element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice']} />,
        children: [{ index: true, element: <StatistichePage /> }],
      },

      {
        path: 'statistiche-app',
        element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice']} />,
        children: [{ index: true, element: <StatisticheAppPage /> }],
      },

      {
        path: 'group',
        element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice']} />,
        children: [
          { index: true, path: 'list', element: <GruppoListPage /> },
          { path: 'new', element: <GruppoCreatePage /> },
          { path: ':id/edit', element: <GruppoEditPage /> },
          { path: ':id', element: <GruppoDetailPage /> },
        ],
      },

      {
        path: 'notification',
        element: <RoleGuard allowedRoles={['Amministratore', 'Backoffice']} />,
        children: [
          { index: true, path: 'list', element: <NotificationListPage /> },
          // { path: 'new', element: <NotificationCreatePage /> },
        ],
      },
    ],
  },
];
